import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import {
  Crosshair,
  Loader2,
  Minus,
  Plus,
  Search,
  User,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { getBrowserLocation } from "@/utils/geoLocationUtils";
import { resolveEasyModeLocationFromCoordinates } from "@/services/googlePlacesRestService";
import { LocationAutocomplete } from "@/components/common/PlaceAutocompleteInput";
import type { EasyModePlaceSelection } from "@/types/easyModePlace";
import {
  FIND_RIDE_COLORS,
  FIND_RIDE_COUNTRY_CODE,
  FIND_RIDE_MAX_SEATS,
  FIND_RIDE_MIN_SEATS,
} from "../constants/findRide.constants";
import { tFindRide } from "../locales/FindRideTranslations";
import { findRideStyles } from "../styles/findRide.styles";
import type { FindRideSearchCriteria } from "../types/findRide.types";
import {
  addDays,
  formatChipDate,
  startOfDay,
  toIsoDate,
} from "../utils/dateHelpers";
import {
  arePlacesDifferent,
  isCompleteEasyModePlace,
} from "../utils/validateFindRidePlace";
import { FindRideCalendarChip, FindRideDateField } from "./FindRideDateField";

type FieldShellProps = {
  label: string;
  children: ReactNode;
};

function FieldShell({ label, children }: FieldShellProps) {
  return (
    <div className="space-y-1.5">
      <p className={findRideStyles.fieldLabel}>{label}</p>
      {children}
    </div>
  );
}

function chipLabel(date: Date, index: number): string {
  if (index === 0) return tFindRide("today");
  if (index === 1) return tFindRide("tomorrow");
  return formatChipDate(date);
}

export type FindRideSearchFormProps = {
  initialCriteria?: FindRideSearchCriteria | null;
  isSearching?: boolean;
  submitError?: string | null;
  onSubmitSearch: (criteria: FindRideSearchCriteria) => void;
  /** Clears prior search message / restores CTA when any field changes. */
  onCriteriaChange?: () => void;
};

export function FindRideSearchForm({
  initialCriteria = null,
  isSearching = false,
  submitError = null,
  onSubmitSearch,
  onCriteriaChange,
}: FindRideSearchFormProps) {
  const today = useMemo(() => startOfDay(new Date()), []);
  const dateChips = useMemo(
    () => [0, 1, 2, 3].map((offset) => addDays(today, offset)),
    [today]
  );

  const [pickupPlace, setPickupPlace] = useState<EasyModePlaceSelection | null>(
    initialCriteria?.pickup ?? null
  );
  const [dropPlace, setDropPlace] = useState<EasyModePlaceSelection | null>(
    initialCriteria?.drop ?? null
  );
  const [travelDate, setTravelDate] = useState(
    initialCriteria?.date ?? toIsoDate(today)
  );
  const [seats, setSeats] = useState(
    Math.max(FIND_RIDE_MIN_SEATS, initialCriteria?.seats ?? 1)
  );
  const [detectingLocation, setDetectingLocation] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (!initialCriteria) return;
    setPickupPlace(initialCriteria.pickup);
    setDropPlace(initialCriteria.drop);
    setTravelDate(initialCriteria.date);
    setSeats(Math.max(FIND_RIDE_MIN_SEATS, initialCriteria.seats));
  }, [initialCriteria]);

  const markCriteriaChanged = useCallback(() => {
    setFormError(null);
    onCriteriaChange?.();
  }, [onCriteriaChange]);

  const handleMyLocation = useCallback(async () => {
    setLocationError(null);
    setDetectingLocation(true);
    try {
      const { lat, lng } = await getBrowserLocation({ timeout: 10000 });
      const place = await resolveEasyModeLocationFromCoordinates(lat, lng);
      setPickupPlace(place);
      markCriteriaChanged();
      setLocationError(null);
    } catch {
      setLocationError(tFindRide("locationDenied"));
    } finally {
      setDetectingLocation(false);
    }
  }, [markCriteriaChanged]);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (isSearching) return;

    if (
      !isCompleteEasyModePlace(pickupPlace) ||
      !isCompleteEasyModePlace(dropPlace)
    ) {
      setFormError(tFindRide("selectPickupDrop"));
      return;
    }

    if (!travelDate) {
      setFormError(tFindRide("selectDate"));
      return;
    }

    if (seats < FIND_RIDE_MIN_SEATS) {
      setFormError(tFindRide("selectSeatsError"));
      return;
    }

    if (!arePlacesDifferent(pickupPlace, dropPlace)) {
      setFormError(tFindRide("pickupDropDifferent"));
      return;
    }

    setFormError(null);
    onSubmitSearch({
      pickup: pickupPlace,
      drop: dropPlace,
      date: travelDate,
      seats,
    });
  };

  const displayError = formError || submitError;

  return (
    <form onSubmit={handleSubmit} className={findRideStyles.card} noValidate>
      <div className="space-y-4">
        <FieldShell label={tFindRide("pickupLabel")}>
          <LocationAutocomplete
            id="find-ride-pickup"
            label={tFindRide("pickupLabel")}
            hideLabel
            placeholder={tFindRide("pickupPlaceholder")}
            mode="origin"
            restrictToCountryCode={FIND_RIDE_COUNTRY_CODE}
            value={pickupPlace}
            onEasyModePlaceSelect={(place) => {
              setPickupPlace(place);
              setLocationError(null);
              markCriteriaChanged();
            }}
            onClear={() => {
              setPickupPlace(null);
              markCriteriaChanged();
            }}
            onInteraction={() => {
              setLocationError(null);
              markCriteriaChanged();
            }}
            clearable={false}
            isLoading={detectingLocation}
            pinClassName="h-4 w-4 text-[#22C55E]"
            inputClassName="h-auto rounded-xl py-3 text-sm"
            endAdornment={
              <button
                type="button"
                onClick={handleMyLocation}
                disabled={detectingLocation || isSearching}
                className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg px-1.5 py-1 text-xs font-semibold transition-colors hover:bg-[#E8F5EE] disabled:cursor-not-allowed disabled:opacity-60 sm:gap-1"
                style={{ color: FIND_RIDE_COLORS.pageGreen }}
              >
                {detectingLocation ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                ) : (
                  <Crosshair className="h-3.5 w-3.5" aria-hidden />
                )}
                <span>{tFindRide("myLocation")}</span>
              </button>
            }
          />
          {locationError && (
            <p className="text-xs text-red-600" role="alert">
              {locationError}
            </p>
          )}
        </FieldShell>

        <FieldShell label={tFindRide("dropLabel")}>
          <LocationAutocomplete
            id="find-ride-drop"
            label={tFindRide("dropLabel")}
            hideLabel
            placeholder={tFindRide("dropPlaceholder")}
            mode="destination"
            restrictToCountryCode={FIND_RIDE_COUNTRY_CODE}
            value={dropPlace}
            onEasyModePlaceSelect={(place) => {
              setDropPlace(place);
              markCriteriaChanged();
            }}
            onClear={() => {
              setDropPlace(null);
              markCriteriaChanged();
            }}
            onInteraction={markCriteriaChanged}
            pinClassName="h-4 w-4 text-[#F59E0B]"
            inputClassName={findRideStyles.fieldInput}
          />
        </FieldShell>

        <FieldShell label={tFindRide("dateLabel")}>
          <FindRideDateField value={travelDate} />

          <div className={findRideStyles.dateChipRow}>
            <div className={findRideStyles.dateChipScroll}>
              {dateChips.map((date, index) => {
                const iso = toIsoDate(date);
                const selected = iso === travelDate;
                return (
                  <button
                    key={iso}
                    type="button"
                    onClick={() => {
                      setTravelDate(iso);
                      markCriteriaChanged();
                    }}
                    disabled={isSearching}
                    className={cn(
                      findRideStyles.dateChip,
                      selected
                        ? "border-transparent text-white"
                        : "border-gray-200 bg-white text-gray-700 hover:border-gray-300"
                    )}
                    style={
                      selected
                        ? { backgroundColor: FIND_RIDE_COLORS.pageGreen }
                        : undefined
                    }
                  >
                    {chipLabel(date, index)}
                  </button>
                );
              })}
            </div>
            <FindRideCalendarChip
              value={travelDate}
              min={toIsoDate(today)}
              disabled={isSearching}
              onChange={(iso) => {
                setTravelDate(iso);
                markCriteriaChanged();
              }}
            />
          </div>
        </FieldShell>

        <FieldShell label={tFindRide("seatsLabel")}>
          <div className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white px-3 py-2.5">
            <span className="inline-flex min-w-0 items-center gap-2 text-sm text-gray-600">
              <User
                className="h-4 w-4 shrink-0"
                style={{ color: FIND_RIDE_COLORS.pageGreen }}
                aria-hidden
              />
              <span className="truncate">{tFindRide("selectSeats")}</span>
            </span>
            <div className="flex shrink-0 items-center gap-2 sm:gap-3">
              <button
                type="button"
                onClick={() => {
                  setSeats((n) => Math.max(FIND_RIDE_MIN_SEATS, n - 1));
                  markCriteriaChanged();
                }}
                disabled={seats <= FIND_RIDE_MIN_SEATS || isSearching}
                className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                aria-label="Decrease seats"
              >
                <Minus className="h-4 w-4" />
              </button>
              <span
                className="min-w-[1.25rem] text-center text-base font-semibold text-gray-900"
                aria-live="polite"
              >
                {seats}
              </span>
              <button
                type="button"
                onClick={() => {
                  setSeats((n) => Math.min(FIND_RIDE_MAX_SEATS, n + 1));
                  markCriteriaChanged();
                }}
                disabled={seats >= FIND_RIDE_MAX_SEATS || isSearching}
                className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                aria-label="Increase seats"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
          </div>
        </FieldShell>

        {displayError && (
          <p className={findRideStyles.errorText} role="alert">
            {displayError}
          </p>
        )}

        <button
          type="submit"
          disabled={isSearching}
          className={findRideStyles.primaryCta}
          style={{ backgroundColor: FIND_RIDE_COLORS.ctaGreen }}
        >
          {isSearching ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
              {tFindRide("searchingCta")}
            </>
          ) : (
            <>
              <Search className="h-5 w-5" aria-hidden />
              {tFindRide("findRideCta")}
            </>
          )}
        </button>
      </div>
    </form>
  );
}
